import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-location';

interface Booking {
    id: number;
    guestName: string;
    checkin: string;
    checkout: string;
    revenue: number;
    currency: string;
    cleaningFee: number;
}

const DayBookingsList = ({
    monthDay,
    bookings,
}: {
    monthDay: string;
    bookings: Booking[];
}) => {
    if (bookings.length === 0) {
        return (
            <Box p={3}>
                <Typography variant="h6" gutterBottom>
                    {monthDay}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    No bookings for this day
                </Typography>
            </Box>
        );
    }

    const calculateDays = (checkin: string, checkout: string): number => {
        const checkinDate = new Date(`${checkin}T00:00:00Z`);
        const checkoutDate = new Date(`${checkout}T00:00:00Z`);
        const diffInMs = checkoutDate.getTime() - checkinDate.getTime();
        return Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    };

    const sortedBookings = [...bookings].sort((a, b) => {
        return new Date(b.checkin).getTime() - new Date(a.checkin).getTime();
    });

    return (
        <Box p={3}>
            <Typography variant="h6" gutterBottom>
                {monthDay} - {bookings.length} booking
                {bookings.length !== 1 ? 's' : ''}
            </Typography>
            <List>
                {sortedBookings.map((booking, index) => {
                    const days = calculateDays(
                        booking.checkin,
                        booking.checkout
                    );
                    const pricePerDay =
                        days > 0
                            ? (booking.revenue / days).toFixed(2)
                            : booking.revenue;

                    const periodText = `${booking.checkin} → ${booking.checkout}`;

                    return (
                        <Box key={booking.id}>
                            {index > 0 && <Divider />}
                            <Link to={`/booking/${booking.id}`}>
                                <ListItem
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: 'action.hover',
                                        },
                                    }}
                                >
                                    <Box display="flex" flexDirection="row">
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            mr={2}
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Typography
                                                color="text.secondary"
                                                variant="body2"
                                                fontSize={18}
                                            >
                                                {pricePerDay} {booking.currency}
                                                /day
                                            </Typography>
                                        </Box>
                                        <ListItemText
                                            primary={booking.guestName}
                                            secondary={
                                                <>
                                                    <Typography
                                                        component="span"
                                                        variant="body2"
                                                        color="text.primary"
                                                    >
                                                        {periodText}
                                                    </Typography>
                                                    <br />
                                                    {booking.revenue}{' '}
                                                    {booking.currency}
                                                </>
                                            }
                                        />
                                    </Box>
                                </ListItem>
                            </Link>
                        </Box>
                    );
                })}
            </List>
        </Box>
    );
};

export default DayBookingsList;
